<script>
    import { createEventDispatcher } from "svelte";
    import { slide } from 'svelte/transition';
    
    import { getMonthName, getDayName, getCalendarDateStringAsDate, getApiDateFormat, 
      getUkCalendarDateStringAsIsoDateString } from "../date-time.js";
      
    import Calendar from "./Calendar.svelte";
  
    const dispatch = createEventDispatcher();
  
    // props
    export let isAllowed = () => true;
    export let selected = new Date();
    
    // state
    let date, month, year, showDatePicker;
    let displayDate = selected.getDate() + " " + getMonthName(selected.getMonth()) + " " + selected.getFullYear();
  
    // so that these change with props
    $: {
      date = selected.getDate();
      month = selected.getMonth();
      year = selected.getFullYear();
    }
  
    // handlers
    const openDatePicker = () => {
      showDatePicker = true;
    };

    const onKeydown = (e) => {
      if (e.key == "Escape") showDatePicker = false;
      
      if (e.key == "Tab" && e.shiftKey) showDatePicker = false; 

      if (e.key == "Enter") {
        // We need to deal with UK based dates so flip month and day around
        let dateString = displayDate.includes("/") ? getUkCalendarDateStringAsIsoDateString(displayDate) : displayDate;

        if (!isNaN(Date.parse(dateString))) {
          let newDate;
          if (dateString.includes("/")) {
              newDate = getCalendarDateStringAsDate(dateString);
          } else {
              newDate = new Date(Date.parse(dateString));
          }
          // If the day is the same then enter should just open the date picker
          if (getApiDateFormat(newDate) == getApiDateFormat(selected)) {
            showDatePicker = true;
          }
          // Otherwise it should fire off a date change event
          else {
            showDatePicker = false;
            displayDate = newDate.getDate() + " " + getMonthName(newDate.getMonth()) + " " + newDate.getFullYear()
            dispatch("datechange", newDate);
          }
        }
        // If the entered date is invalid, open the date picker but restore the date string to the previous value
        else {
          showDatePicker = true;
          displayDate = selected.getDate() + " " + getMonthName(selected.getMonth()) + " " + selected.getFullYear();
        }
      }
    };
  
    const next = () => {
      if (month === 11) {
        month = 0;
        year = year + 1;
        return;
      }
      month = month + 1;
    };
  
    const prev = () => {
      if (month === 0) {
        month = 11;
        year -= 1;
        return;
      }
      month -= 1;
    };

    const now = () => {
      month = (new Date()).getMonth();
      year = (new Date()).getFullYear();  
      showDatePicker = false;
      displayDate = (new Date()).getDate() + " " + getMonthName((new Date()).getMonth()) + " " + (new Date()).getFullYear()
      dispatch("datechange", new Date());
    };
  
    const onDateChange = d => {
      showDatePicker = false;
      displayDate = d.detail.getDate() + " " + getMonthName(d.detail.getMonth()) + " " + d.detail.getFullYear();
      dispatch("datechange", d.detail);
    };

    const isToday = d => {
      return d.getDate() === (new Date()).getDate() && d.getMonth() === (new Date()).getMonth() && year === (new Date()).getFullYear();
    }

    const isYesterday = d => {
      return (d.getDate() + 1) === (new Date()).getDate() && d.getMonth() === (new Date()).getMonth() && year === (new Date()).getFullYear();
    }
  </script>
  
  <style>
    .relative {
      position: fixed;
      top: 8px;
      right: 86px;
      z-index: 1029;
    }

    .friendly-date {
      position: fixed;
      right: 288px;
      top: 15px;
      z-index: 1029;
      color: white;
      font-size: 12px;
      font-weight: 600;
      cursor: default;
    }

    .friendly-date.badge-light {
      background-color: white;
      border-radius: 10px;
      padding: 4px 8px;
    }

    .friendly-date.badge-light.date-past {
      background-color: #fa8072;
    }

    .box {
      position: absolute;
      top: 32px;
      right: 0px;
      border: 1px solid #ced4da;
      display: inline-block;
      background-color: white;
      z-index: 1032;
    }
  
    .month-name {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin: 0;
      padding: 8px 76px;
      background-color: #3d5980;
      color: #ffffff;
    }
  
    .month-name button.focus-outline:focus-visible {
      box-shadow: 0 0 0 0.2rem rgba(255,255,255,.5);
    }

    .center {
      display: flex;
      justify-content: center;
      align-items: center;
    }

    input, input:focus {
      position: relative;
      color: white;
      border-color: white;
      background: none;
      z-index: 1029;
      cursor: default;
    }

    .calendar-img {
      position: absolute;
      right: 8px;
      top: 9px;
      z-index: 1030;
    }

    #date-picker-backdrop {
      position: fixed;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      background: rgba(0, 0, 0, 0);
      z-index: 1031;
    }

    .month-icon {
      width: 25px;
      cursor: pointer;
    }

    .btn-today {
      align-items: center;
      width: 100%;
      margin: 0;
      padding: 8px;
      border-radius: 0%;
      border: 0;
      background-color: #f5f5f5;
    }

    .btn-today:hover {
      background-color: #dcdcdc;
      transition: 0.3s;
    }

    .btn-today:focus {
      box-shadow: none;
    }

    .btn-today:focus-visible {
      box-shadow: 0 0 0 0.2rem rgba(0,123,255,.5);
    }

    .month-icon:hover {
      opacity: 0.8;
      transition: 0.3s;
    }
  </style>

  <div class="friendly-date" class:date-past={!isToday(selected)} class:badge={!isToday(selected)} class:badge-light={!isToday(selected)}>
    {#if isToday(selected)}
      Today
    {:else if isYesterday(selected)}
      Yesterday
    {:else}
      {getDayName(selected.getDay())}
    {/if}
  </div>

  <div class="relative">  
    <input type="text" class="form-control" on:click={openDatePicker} on:keydown={onKeydown} bind:value={displayDate} onfocus="this.select();" />
    <img src="/assets/calendar.svg" class="calendar-img" alt="Select report date" on:click={openDatePicker} on:keydown={openDatePicker} />

    {#if showDatePicker}
      <div class="box" transition:slide={{ duration: 600 }}>
        <div class="month-name">
          <div class="center">
              <button class="button-reset focus-outline" on:click={prev} on:keydown={(e) => {if (e.key == "Escape") showDatePicker = false}}>
                <img src="/assets/arrow/arrow-left-circle-inverse.svg" alt="Previous month" class="month-icon"/>
              </button>
          </div>
          <div class="center">{getMonthName(month)} {year}</div>
          <div class="center">
            <button class="button-reset focus-outline" on:click={next} on:keydown={(e) => {if (e.key == "Escape") showDatePicker = false}}>
              <img src="/assets/arrow/arrow-right-circle-inverse.svg" alt="Next month" class="month-icon"/>
            </button>
          </div>
        </div>
        <Calendar
          {month}
          {year}
          date={selected}
          {isAllowed}
          on:datechange={onDateChange}
          on:escape={() => showDatePicker = false}
          on:tabout={() => {if (isToday(selected)) showDatePicker = false}}/>
        {#if !isToday(selected)}
          <button on:click={now} class="btn btn-light btn-today" 
            on:keydown={(e) => {if (e.key == "Escape" || e.key == "Tab") showDatePicker = false}}>Today</button>
        {/if}
      </div>

      <div id="date-picker-backdrop" on:click={() => showDatePicker = false} on:keydown={() => showDatePicker = false}></div>
    {/if}
  </div>